import React from "react";
import {
  Button,
  Grid,
  Group,
  TextInput,
  PasswordInput,
  Select,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { useAddOrganization } from "api/superAdmin/addOrganization";
import { useUser } from "hooks/useUser";
import { CANADA_TIMEZONES, DEFAULT_ORG_TIMEZONE } from "utils/timezoneDate";

const roleOptions = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "user", label: "User" },
];

const organizationSchema = z.object({
  organizationName: z
    .string()
    .trim()
    .min(1, { message: "Please enter organization name" }),
  timezone: z.string().min(1, { message: "Please select a timezone" }),
  defaultUser: z.object({
    firstName: z.string().trim().min(1, { message: "Please enter first name" }),
    lastName: z.string().trim().min(1, { message: "Please enter last name" }),
    email: z.string().trim().email({ message: "Please enter a valid email" }),
    password: z
      .string()
      .min(8, { message: "Password should be at least 8 characters" }),
    role: z.enum(["admin", "manager", "user"], {
      errorMap: () => ({ message: "Please select a role" }),
    }),
  }),
});

type OrganizationFormValues = z.infer<typeof organizationSchema>;

export default function AddOrganizationForm({
  onClose,
}: {
  onClose: () => void;
}) {
  const { user } = useUser();

  const { mutate: addOrganization, isLoading } = useAddOrganization();

  const form = useForm<OrganizationFormValues>({
    initialValues: {
      organizationName: "",
      timezone: DEFAULT_ORG_TIMEZONE,
      defaultUser: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        role: "admin",
      },
    },
    validate: zodResolver(organizationSchema),
  });

  const handleSubmit = (values: OrganizationFormValues) => {
    addOrganization(values, {
      onSuccess: () => {
        form.reset();
        onClose();
      },
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            withAsterisk
            label="Organization Name"
            placeholder="Organization Name"
            {...form.getInputProps("organizationName")}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Select
            withAsterisk
            label="Timezone"
            description="Attendance start/end times are interpreted in this timezone"
            placeholder="Select timezone"
            data={CANADA_TIMEZONES}
            searchable
            {...form.getInputProps("timezone")}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            withAsterisk
            label=" First Name"
            {...form.getInputProps("defaultUser.firstName")}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            withAsterisk
            label=" Last Name"
            {...form.getInputProps("defaultUser.lastName")}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <TextInput
            withAsterisk
            label="Email"
            placeholder="admin@techsolutions.com"
            {...form.getInputProps("defaultUser.email")}
          />
        </Grid.Col>

        <Grid.Col span={8}>
          <PasswordInput
            withAsterisk
            label="Password"
            placeholder="At least 8 characters"
            {...form.getInputProps("defaultUser.password")}
          />
        </Grid.Col>

        <Grid.Col span={4}>
          <Select
            withAsterisk
            label="Role"
            disabled
            data={roleOptions}
            {...form.getInputProps("defaultUser.role")}
          />
        </Grid.Col>
      </Grid>

      <Group position="right" spacing={20} mt={10}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="filled"
          disabled={!form.isDirty()}
          loading={isLoading}
        >
          Add Organization
        </Button>
      </Group>
    </form>
  );
}
